import { useMutation, type UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { getServiceAgreementList } from 'services/private-banking/privateBankingService';
import { getEligiblePrivateBankingProducts } from 'services/private-banking/getEligiblePrivateBankingProducts';
import {
  type CreateServiceAgreementResponse,
  type PrivateBankingProductCatalogueListResult,
  ServiceAgreementListCategory,
  type ServiceAgreementV2,
  type ServiceDefinitionNode,
} from 'pco-product-service-internal-consumer';
import { getCRMCHDirectLODLink } from 'services/private-banking/getCRMCHDirectLODLink';
import { createServiceAgreementApi } from 'services/service-agreement/createServiceAgreementApi';
import { partnersApi, serviceAgreementApi } from 'api';

const PARTNER_SEARCH_BY_BR_PAGE_SIZE = 50;

export const SERVICE_AGREEMENT_LIST_QUERY_KEY = 'serviceAgreementList';

export const useGetServiceAgreementList = (params: {
  partnerUid?: string;
  businessRelationUid?: string;
  accountNumber?: string;
}) => {
  return useQuery({
    queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY, params],
    queryFn: () => getServiceAgreementList(params),
    enabled: !!(params.partnerUid || params.businessRelationUid || params.accountNumber),
  });
};

export const COUNTRY_RISK_WAIVERS_QUERY_KEY = 'countryRiskWaivers';

export const useGetCountryRiskWaivers = (brUid: string | undefined) => {
  return useQuery({
    queryKey: [COUNTRY_RISK_WAIVERS_QUERY_KEY, brUid],
    queryFn: () =>
      getServiceAgreementList({ businessRelationUid: brUid, category: ServiceAgreementListCategory.Waivers }),
    enabled: !!brUid,
  });
};

export const getServiceAgreementDetailsQueryKey = (serviceAgreementUid: string | undefined) =>
  ['serviceAgreementDetails', serviceAgreementUid] as const;

export const getServiceAgreementHistoryQueryKey = (serviceAgreementUid: string | undefined) =>
  ['serviceAgreementHistory', serviceAgreementUid] as const;

export const fetchServiceAgreementDetails = async (serviceAgreementUid: string) =>
  await serviceAgreementApi.getServiceAgreement({ serviceAgreementUid });

export const useUpdateServiceAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceAgreement, brUid }: { serviceAgreement: ServiceAgreementV2; brUid: string }) => {
      if (!serviceAgreement.serviceAgreementId) {
        throw new Error('serviceAgreementId is required');
      }

      return await serviceAgreementApi.closeServiceAgreement({
        brUid,
        serviceAgreementsClosingRequest: { idList: [serviceAgreement.serviceAgreementId] },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] }),
  });
};

export const useDeactivateWealthPlanningService = () =>
  useMutation({
    mutationFn: async ({ serviceAgreement, brUid }: { serviceAgreement: ServiceAgreementV2; brUid: string }) => {
      if (!serviceAgreement.serviceAgreementId) {
        throw new Error('serviceAgreementId is required');
      }

      return await serviceAgreementApi.deactivateWealthPlanningService({
        serviceAgreementUid: serviceAgreement.serviceAgreementId,
        brUid,
      });
    },
  });

export type CreatePrivateBankingServiceAgreementsVariables = {
  brUid: string | undefined;
  serviceDefinitions: ServiceDefinitionNode[];
  contactNoteUids?: string[];
};

type CreatePrivateBankingServiceAgreementsResult = CreateServiceAgreementResponse[];

export const useCreatePrivateBankingServiceAgreements = (): UseMutationResult<
  CreatePrivateBankingServiceAgreementsResult,
  Error,
  CreatePrivateBankingServiceAgreementsVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<
    CreatePrivateBankingServiceAgreementsResult,
    Error,
    CreatePrivateBankingServiceAgreementsVariables
  >({
    mutationFn: ({
      brUid,
      serviceDefinitions,
      contactNoteUids,
    }: CreatePrivateBankingServiceAgreementsVariables): Promise<CreatePrivateBankingServiceAgreementsResult> => {
      if (!brUid) {
        throw new Error('brUid is required');
      }

      return Promise.all(
        serviceDefinitions.map((serviceDefinition) =>
          createServiceAgreementApi(undefined, brUid, serviceDefinition, contactNoteUids),
        ),
      );
    },
    onSuccess: async (_, { brUid }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['eligiblePrivateBankingProducts', brUid] }),
      ]);
    },
  });
};

export const useGetServiceAgreementDetails = (serviceAgreementUid: string | undefined) => {
  return useQuery({
    queryKey: getServiceAgreementDetailsQueryKey(serviceAgreementUid),
    queryFn: () => {
      if (!serviceAgreementUid) {
        throw new Error('serviceAgreementUid is required');
      }

      return fetchServiceAgreementDetails(serviceAgreementUid);
    },
    enabled: !!serviceAgreementUid,
  });
};

export const useGetEligiblePrivateBankingProducts = (brUid: string | undefined) => {
  return useQuery<PrivateBankingProductCatalogueListResult>({
    queryKey: ['eligiblePrivateBankingProducts', brUid],
    queryFn: () => {
      if (!brUid) {
        throw new Error('brUid is required');
      }

      return getEligiblePrivateBankingProducts(brUid);
    },
    enabled: !!brUid,
  });
};

export const useGetCRMCHDirectLODLink = (brUid: string | undefined) => {
  return useQuery({
    queryKey: ['crmchDirectLodLink', brUid],
    queryFn: () => {
      if (!brUid) {
        throw new Error('brUid is required');
      }

      return getCRMCHDirectLODLink(brUid);
    },
    enabled: !!brUid,
  });
};

export const useGetServiceAgreementHistory = (serviceAgreementUid: string | undefined) => {
  return useQuery({
    queryKey: getServiceAgreementHistoryQueryKey(serviceAgreementUid),
    queryFn: async () => {
      if (!serviceAgreementUid) {
        throw new Error('serviceAgreementUid is required');
      }

      return await serviceAgreementApi.getServiceAgreementHistory({ serviceAgreementUid });
    },
    enabled: !!serviceAgreementUid,
  });
};

export const useSearchPartnersByBr = (
  brUid: string | undefined,
  searchTerm: string,
  enabled: boolean,
  p2pDepth = 0,
) => {
  const normalizedSearchTerm = searchTerm.trim();

  return useQuery({
    queryKey: ['partnerSearchByBr', brUid, normalizedSearchTerm, p2pDepth],
    queryFn: async () => {
      if (!brUid) {
        throw new Error('brUid is required');
      }

      return await partnersApi.searchPartnersBr({
        brUid,
        partnersBrSearchRequest: {
          searchTerm: normalizedSearchTerm,
          limitResults: PARTNER_SEARCH_BY_BR_PAGE_SIZE,
          p2pDepth,
        },
      });
    },
    enabled: !!brUid && enabled,
  });
};
